'use client';

import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createClient } from '../lib/supabase/client';

// Demo credentials — auto-logout after 60 min
const DEMO_EMAIL = 'dr.hoffmann@juraproof.de';
const DEMO_SESSION_MINUTES = 60;

export type SubscriptionStatus = 'trial' | 'active' | 'cancelled' | 'expired';

export interface SubscriptionInfo {
  plan: 'trial' | 'annual';
  status: SubscriptionStatus;
  trialStart: Date | null;
  trialEnd: Date | null;
  subscriptionStart: Date | null;
  subscriptionEnd: Date | null;
  cancellationRequested: boolean;
  daysLeftInTrial: number | null;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  isTrialExpiringSoon: boolean; // <= 1 day left
  isLicensed: boolean;
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function computeSubscriptionInfo(row: any): SubscriptionInfo {
  const now = new Date();
  const trialEnd = row?.trial_end ? new Date(row.trial_end) : null;
  const trialStart = row?.trial_start ? new Date(row.trial_start) : null;
  const subscriptionStart = row?.subscription_start ? new Date(row.subscription_start) : null;
  const subscriptionEnd = row?.subscription_end ? new Date(row.subscription_end) : null;

  const daysLeftInTrial = trialEnd
    ? Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const isTrialActive =
    row?.status === 'trial' && trialEnd !== null && trialEnd > now;
  const isTrialExpired =
    row?.status === 'trial' && trialEnd !== null && trialEnd <= now;
  const isTrialExpiringSoon =
    isTrialActive && daysLeftInTrial !== null && daysLeftInTrial <= 1;
  const isLicensed = row?.status === 'active';

  return {
    plan: row?.plan ?? 'trial',
    status: row?.status ?? 'trial',
    trialStart,
    trialEnd,
    subscriptionStart,
    subscriptionEnd,
    cancellationRequested: row?.cancellation_requested ?? false,
    daysLeftInTrial,
    isTrialActive,
    isTrialExpired,
    isTrialExpiringSoon,
    isLicensed,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const demoLogoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Use a ref so the client is created once and never triggers re-renders
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const isDemoUser = useCallback((u: any) => {
    return u?.email === DEMO_EMAIL;
  }, []);

  const clearDemoTimer = useCallback(() => {
    if (demoLogoutTimer.current) {
      clearTimeout(demoLogoutTimer.current);
      demoLogoutTimer.current = null;
    }
  }, []);

  const loadSubscription = useCallback(async (userId: string) => {
    try {
      const { data } = await supabaseRef.current
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();
      setSubscription(data ? computeSubscriptionInfo(data) : null);
    } catch {
      setSubscription(null);
    }
  }, []);

  const startDemoLogoutTimer = useCallback((signOutFn: () => Promise<void>) => {
    clearDemoTimer();
    demoLogoutTimer.current = setTimeout(async () => {
      await signOutFn();
    }, DEMO_SESSION_MINUTES * 60 * 1000);
  }, [clearDemoTimer]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await loadSubscription(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription: authSub }
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await loadSubscription(session.user.id);
      } else {
        setSubscription(null);
        clearDemoTimer();
      }
      setLoading(false);
    });

    return () => {
      authSub.unsubscribe();
      clearDemoTimer();
    };
  }, []);

  // Email/Password Sign Up — creates 3-day trial subscription
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;

    // Create 3-day trial subscription for new user
    if (data.user) {
      const trialStart = new Date();
      const trialEnd = new Date(trialStart.getTime() + 3 * 24 * 60 * 60 * 1000);
      await supabase.from('user_subscriptions').upsert({
        user_id: data.user.id,
        plan: 'trial',
        status: 'trial',
        trial_start: trialStart.toISOString(),
        trial_end: trialEnd.toISOString(),
        auto_renew: true,
        agb_accepted: false,
        cancellation_requested: false,
      }, { onConflict: 'user_id' });
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;

    // Start demo auto-logout timer
    if (data.user?.email === DEMO_EMAIL) {
      startDemoLogoutTimer(async () => {
        await supabase.auth.signOut();
        // Redirect to login
        if (typeof window !== 'undefined') {
          window.location.href = '/sign-up-login-screen?demo_timeout=1';
        }
      });
    }

    return data;
  };

  // Sign Out
  const signOut = async () => {
    clearDemoTimer();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Request cancellation
  const requestCancellation = async () => {
    if (!user) return;
    await supabase
      .from('user_subscriptions')
      .update({ cancellation_requested: true, cancelled_at: new Date().toISOString() })
      .eq('user_id', user.id);
    await loadSubscription(user.id);
  };

  // Refresh subscription state
  const refreshSubscription = async () => {
    if (user) await loadSubscription(user.id);
  };

  const value = {
    user,
    session,
    loading,
    subscription,
    isDemoUser: isDemoUser(user),
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    requestCancellation,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
